import React, { ComponentProps, useCallback, useMemo } from "react";
import { Formik, Form, FormikErrors } from "formik";
import { useMutation, useQueryClient } from "react-query";
import { brigadeFormSchema } from "../../validation/objects/brigadeSchema";
import { updatedDiff } from "deep-object-diff";

import { LoadingButton } from "../LoadingButton/LoadingButton";

import { adminService } from "../../services/adminService";

import { apiErrorService } from "../../services/apiErrorService";
import { AppError, isApiError } from "../../types/Error";
import { toastService } from "../../services/toastService";
import { ApplicationImageUploader } from "../ApplicationImageUploader";
import { TextField } from "../TextField";
import { useNavigate } from "react-router-dom";
import { useSelectedBrigade } from "../../hooks/brigades/use-selected-brigade";
import { Brigade, BrigadeForm as TBrigadeForm } from "../../types/brigade";
import { getBrigadeFormValues } from "../../helpers/getBrigadeFormValues";

const initialValuesForCreation: TBrigadeForm = {
  name: "",
  image: null,
};

type CreateVariables = {
  payload: TBrigadeForm;
  setErrors: (errors: FormikErrors<TBrigadeForm>) => void;
  resetForm: () => void;
};

type UpdateVariables = {
  id: number;
  payload: TBrigadeForm;
  setErrors: (errors: FormikErrors<TBrigadeForm>) => void;
};

export const BrigadeForm: React.FC = () => {
  const navigate = useNavigate();
  const currentBrigade = useSelectedBrigade();
  const queryClient = useQueryClient();

  const { isLoading: isCreating, mutate: create } = useMutation<
    Brigade,
    AppError,
    CreateVariables
  >({
    mutationFn: async ({ payload }) => {
      return adminService.createBrigade(payload);
    },
    onSuccess: async (_, { resetForm }) => {
      await queryClient.invalidateQueries({ queryKey: ["brigades"] });
      resetForm();
      toastService.success({ title: "Бригаду успішно створено" });
    },
    onError: (error, { setErrors }) => {
      const message = apiErrorService.getMessage(
        error,
        "Не вдалося створити бригаду",
      );

      if (isApiError(error) && error.response) {
        setErrors(error.response.data.errors);
      }

      toastService.error(message);
    },
    onMutate: () => toastService.dismiss(),
  });

  const { isLoading: isUpdating, mutate: update } = useMutation<
    Brigade,
    AppError,
    UpdateVariables
  >({
    mutationFn: async ({ id, payload }) => {
      return adminService.updateBrigade({ id, ...payload });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["brigades"] });
      toastService.success({ title: "Бригаду успішно змінено" });
    },
    onError: (error, { setErrors }) => {
      const message = apiErrorService.getMessage(
        error,
        "Не вдалося редагувати бригаду",
      );

      if (isApiError(error) && error.response) {
        setErrors(error.response.data.errors);
      }

      toastService.error(message);
    },
    onMutate: () => toastService.dismiss(),
  });

  const initialValues: TBrigadeForm = useMemo(
    () =>
      currentBrigade
        ? getBrigadeFormValues(currentBrigade)
        : initialValuesForCreation,
    [currentBrigade],
  );

  const onSubmit: ComponentProps<typeof Formik<TBrigadeForm>>["onSubmit"] =
    useCallback(
      (values, { setErrors, resetForm }) => {
        if (currentBrigade) {
          const diff = updatedDiff(
            initialValues,
            values,
          ) as Partial<TBrigadeForm>;

          if (Object.keys(diff).length < 1) {
            toastService.articlesToast.warning({
              title: "Жодне з полів не було змінено",
            });

            return;
          }

          update({ id: currentBrigade.id, payload: diff as any, setErrors });
        } else {
          create({ payload: values, setErrors, resetForm });
        }
      },
      [currentBrigade, create],
    );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={brigadeFormSchema}
      onSubmit={onSubmit}
    >
      <Form className="px-4 pt-3">
        <div className="d-flex justify-content-between pe-3">
          <h2 className="display-6 mb-4 ">
            {currentBrigade ? "Редагування бригади" : "Створення бригади"}
          </h2>

          {currentBrigade && (
            <button
              type="button"
              className="btn btn-light d-inline-block"
              style={{ height: 40 }}
              onClick={() => navigate("/admin/brigades")}
            >
              Створити бригаду
              <i className="bi bi-box-arrow-in-up-right ps-2" />
            </button>
          )}
        </div>

        <div className="mb-2">
          <TextField
            name="name"
            className="py-1 border-2"
            placeholder="Введіть назву бригади..."
          />
        </div>

        <div className="mb-2">
          <ApplicationImageUploader
            name="image"
            imagePreviewMaxHeight={500}
            image={currentBrigade?.image}
          />
        </div>

        {currentBrigade ? (
          <LoadingButton
            type="submit"
            isLoading={isUpdating}
            disabled={isUpdating}
          >
            Зберегти
          </LoadingButton>
        ) : (
          <LoadingButton
            isLoading={isCreating}
            disabled={isCreating}
            type="submit"
          >
            Створити
          </LoadingButton>
        )}
      </Form>
    </Formik>
  );
};
